import { useMemo, useState } from 'react'
import { usePageInfo } from 'app/frontend/base/config/queries'
import { wowheadUrl } from 'app/frontend/base/helpers'
import Card from 'app/frontend/base/components/Card'
import ItemIcon from 'app/frontend/base/components/ItemIcon'
import UpgradeTrack from 'app/frontend/types/custom/UpgradeTrack'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'
import {
  isChoiceObtained,
  TrinketItemInfo,
  TrinketMemberRow,
  trinketRankLabels,
  trinketRanks,
  TrinketRank,
} from './trinketPriorityHelpers'

type Props = {
  rows: TrinketMemberRow[]
  lookup: Record<number, TrinketItemInfo>
  minimumTrack: UpgradeTrack
  selectedTrinketId?: number
  onSelectTrinket: (id: number) => void
}

const TrinketPrioritiesSummary = ({
  rows,
  lookup,
  minimumTrack,
  selectedTrinketId,
  onSelectTrinket,
}: Props) => {
  const {
    data: { gameVersion },
  } = usePageInfo()
  const [rankFilter, setRankFilter] = useState<'all' | TrinketRank>('all')

  const aggregates = useMemo(() => {
    const counts: Record<number, { picked: number; obtained: number }> = {}
    const ranksToConsider: readonly TrinketRank[] =
      rankFilter === 'all' ? trinketRanks : [rankFilter]

    rows.forEach((row) => {
      ranksToConsider.forEach((rank) => {
        const choice = row.storedPriority[rank]
        if (!choice?.item_id) return
        counts[choice.item_id] ??= { picked: 0, obtained: 0 }
        counts[choice.item_id].picked += 1
        if (isChoiceObtained(choice, row.trinketCache, minimumTrack)) {
          counts[choice.item_id].obtained += 1
        }
      })
    })

    return Object.entries(counts)
      .map(([id, value]) => ({ id: Number(id), ...value }))
      .sort((a, b) => b.picked - a.picked)
  }, [rows, rankFilter, minimumTrack])

  return (
    <aside className='flex flex-col gap-3 w-full min-w-0 lg:w-76 lg:shrink-0'>
      <Card className='space-y-3 py-3 px-4 min-w-0 overflow-hidden'>
        <div className='flex items-center justify-between pb-2 border-b'>
          <span className='text-base font-medium'>Most wanted</span>
        </div>

        <Tabs
          value={rankFilter}
          onValueChange={(value) => setRankFilter(value as 'all' | TrinketRank)}
        >
          <TabsList className='grid w-full grid-cols-4'>
            {['all', ...trinketRanks].map((value) => (
              <TabsTrigger key={value} value={value}>
                {value === 'all'
                  ? 'Any'
                  : trinketRankLabels[value as TrinketRank]}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {aggregates.length === 0 ? (
          <p className='text-sm text-muted-foreground py-2'>
            No trinkets selected yet.
          </p>
        ) : (
          <div className='space-y-1.5'>
            {aggregates.map((aggregate) => {
              const info = lookup[aggregate.id]
              const percentage = aggregate.picked
                ? (aggregate.obtained / aggregate.picked) * 100
                : 0
              const selected = selectedTrinketId === aggregate.id

              return (
                <Card
                  key={aggregate.id}
                  role='button'
                  tabIndex={0}
                  onClick={() => onSelectTrinket(aggregate.id)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      onSelectTrinket(aggregate.id)
                    }
                  }}
                  className={cn(
                    'flex cursor-pointer items-center gap-2 min-w-0 overflow-hidden py-0.5 px-2',
                    selected
                      ? 'border-primary bg-accent hover:bg-accent'
                      : 'hover:border-primary/40',
                  )}
                >
                  <a
                    href={wowheadUrl(gameVersion, undefined, aggregate.id)}
                    target='_blank'
                    rel='noreferrer'
                    className='shrink-0'
                    onClick={(event) => event.stopPropagation()}
                  >
                    <ItemIcon id={aggregate.id} className='w-7 h-7' />
                  </a>
                  <div className='min-w-0 grow'>
                    <div
                      className={cn(
                        'truncate text-sm font-medium',
                        info && `text-quality-${info.quality}`,
                      )}
                    >
                      {info?.name ?? `Item ${aggregate.id}`}
                    </div>
                    <div className='flex w-full items-center gap-2'>
                      <span
                        className={cn(
                          'text-sm tabular-nums w-4 text-right',
                          aggregate.obtained === 0
                            ? 'text-muted-foreground'
                            : aggregate.obtained === aggregate.picked
                            ? 'text-success-foreground'
                            : 'text-warning-foreground',
                        )}
                      >
                        {aggregate.obtained}
                      </span>
                      <div className='h-2 grow rounded-full bg-muted overflow-hidden'>
                        <div
                          className='h-full bg-success-foreground'
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <span className='text-sm tabular-nums w-4'>
                        {aggregate.picked}
                      </span>
                    </div>
                  </div>
                </Card>
              )
            })}
          </div>
        )}
      </Card>
    </aside>
  )
}

export default TrinketPrioritiesSummary
